// StockHeatmap — station × product stock level grid for EMS supply dashboard

import { useState } from "react";

export interface StockCell {
  station: string;
  product: string;
  current: number;
  target: number;
}

export interface StockHeatmapProps {
  cells: StockCell[];
  stations: string[];
  products: string[];
  isLoading?: boolean;
}

function getRatio(current: number, target: number): number {
  if (target <= 0) return 1;
  return current / target;
}

function getCellClasses(ratio: number): string {
  if (ratio >= 1.0)
    return "bg-green-500/75 hover:bg-green-500/90 border-green-600/30 text-green-950 dark:text-green-100";
  if (ratio >= 0.5)
    return "bg-amber-400/75 hover:bg-amber-400/90 border-amber-500/30 text-amber-950 dark:text-amber-100";
  return "bg-red-500/75 hover:bg-red-500/90 border-red-600/30 text-red-950 dark:text-red-100";
}

function getRatioLabel(ratio: number): string {
  if (ratio >= 1.0) return "OK";
  if (ratio >= 0.5) return "Low";
  return "Critical";
}

interface TooltipState {
  station: string;
  product: string;
  current: number;
  target: number;
  ratio: number;
  x: number;
  y: number;
}

const PRODUCT_LABEL_W = 160; // px
const CELL_W = 56; // px
const CELL_H = 36; // px
const COL_HEADER_H = 68; // px for rotated station labels

export default function StockHeatmap({
  cells,
  stations,
  products,
  isLoading = false,
}: StockHeatmapProps) {
  const [tooltip, setTooltip] = useState<TooltipState | null>(null);

  if (isLoading) {
    return (
      <div className="overflow-x-auto">
        <div
          className="space-y-1"
          style={{
            minWidth:
              PRODUCT_LABEL_W + stations.length * (CELL_W + 4) + 16 || 480,
          }}
        >
          <div
            className="flex gap-1"
            style={{ paddingLeft: PRODUCT_LABEL_W, height: COL_HEADER_H }}
          >
            {Array.from({ length: 5 }).map((_, i) => (
              <div
                key={i}
                className="skeleton shrink-0 rounded-sm"
                style={{ width: CELL_W, height: COL_HEADER_H }}
              />
            ))}
          </div>
          {Array.from({ length: 6 }).map((_, r) => (
            <div key={r} className="flex items-center gap-1">
              <div
                className="skeleton h-4 rounded-md shrink-0"
                style={{ width: PRODUCT_LABEL_W - 8 }}
              />
              {Array.from({ length: 5 }).map((_, c) => (
                <div
                  key={c}
                  className="skeleton rounded-sm shrink-0"
                  style={{ width: CELL_W, height: CELL_H }}
                />
              ))}
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (products.length === 0 || stations.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-40 gap-2">
        <p className="text-sm text-theme-muted">No stock data available</p>
        <p className="text-xs text-theme-muted opacity-60">
          Station and product data will appear here after sync
        </p>
      </div>
    );
  }

  // O(1) cell lookup: "product::station"
  const cellMap = new Map<string, StockCell>();
  for (const cell of cells) {
    cellMap.set(`${cell.product}::${cell.station}`, cell);
  }

  return (
    <div className="overflow-x-auto relative">
      <div
        style={{
          minWidth: PRODUCT_LABEL_W + stations.length * (CELL_W + 4) + 16,
        }}
      >
        {/* Column headers — rotated station names */}
        <div
          className="flex"
          style={{ paddingLeft: PRODUCT_LABEL_W, height: COL_HEADER_H }}
        >
          {stations.map((station) => (
            <div
              key={station}
              className="shrink-0 flex items-end justify-center pb-1"
              style={{ width: CELL_W + 4 }}
            >
              <span
                className="text-xs font-medium text-theme-muted whitespace-nowrap origin-bottom-left"
                style={{
                  transform: "rotate(-45deg) translateX(-4px)",
                  display: "inline-block",
                  maxWidth: 80,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
                title={station}
              >
                {station}
              </span>
            </div>
          ))}
        </div>

        {/* Data rows — one row per product */}
        <div className="flex flex-col gap-1">
          {products.map((product) => (
            <div key={product} className="flex items-center gap-1">
              {/* Product label — sticky-style left column */}
              <div
                className="shrink-0 flex items-center"
                style={{ width: PRODUCT_LABEL_W }}
              >
                <span
                  className="text-xs text-theme-secondary font-medium truncate"
                  style={{ maxWidth: PRODUCT_LABEL_W - 8 }}
                  title={product}
                >
                  {product}
                </span>
              </div>

              {/* Cells */}
              {stations.map((station) => {
                const cell = cellMap.get(`${product}::${station}`);
                const current = cell?.current ?? 0;
                const target = cell?.target ?? 0;
                const ratio = getRatio(current, target);
                const cellClasses = getCellClasses(ratio);

                return (
                  <div
                    key={station}
                    className={`shrink-0 rounded-sm border cursor-default transition-colors flex items-center justify-center ${cellClasses}`}
                    style={{ width: CELL_W, height: CELL_H }}
                    onMouseEnter={(e) => {
                      const rect = e.currentTarget.getBoundingClientRect();
                      setTooltip({
                        station,
                        product,
                        current,
                        target,
                        ratio,
                        x: rect.left + rect.width / 2,
                        y: rect.top,
                      });
                    }}
                    onMouseLeave={() => setTooltip(null)}
                    aria-label={`${product} at ${station}: ${current}/${target} (${getRatioLabel(ratio)})`}
                  >
                    <span className="text-xs font-semibold tabular-nums leading-none">
                      {current}
                    </span>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>

      {/* Tooltip */}
      {tooltip && (
        <div
          className="fixed z-50 glass rounded-lg px-3 py-2 text-xs shadow-lg pointer-events-none"
          style={{
            left: tooltip.x,
            top: tooltip.y - 8,
            transform: "translate(-50%, -100%)",
          }}
        >
          <p className="font-semibold text-theme-primary">{tooltip.product}</p>
          <p className="text-theme-muted mt-0.5">{tooltip.station}</p>
          <div className="mt-1.5 flex flex-col gap-0.5">
            <p className="text-theme-secondary">
              Current:{" "}
              <span className="font-bold text-theme-primary">
                {tooltip.current}
              </span>
            </p>
            <p className="text-theme-secondary">
              Target:{" "}
              <span className="font-bold text-theme-primary">
                {tooltip.target}
              </span>
            </p>
            <p className="text-theme-secondary">
              Delta:{" "}
              <span
                className={`font-bold ${
                  tooltip.current - tooltip.target >= 0
                    ? "text-status-green"
                    : tooltip.ratio >= 0.5
                      ? "text-status-amber"
                      : "text-status-red"
                }`}
              >
                {tooltip.current - tooltip.target >= 0 ? "+" : ""}
                {tooltip.current - tooltip.target}
              </span>
            </p>
          </div>
          <p
            className={`font-bold mt-1 ${
              tooltip.ratio >= 1.0
                ? "text-status-green"
                : tooltip.ratio >= 0.5
                  ? "text-status-amber"
                  : "text-status-red"
            }`}
          >
            {getRatioLabel(tooltip.ratio)}
          </p>
        </div>
      )}

      {/* Legend */}
      <div className="flex items-center gap-4 mt-3 px-1">
        {[
          {
            label: "Stocked (≥100%)",
            classes: "bg-green-500/75 border-green-600/30",
          },
          {
            label: "Low (50–99%)",
            classes: "bg-amber-400/75 border-amber-500/30",
          },
          {
            label: "Critical (<50%)",
            classes: "bg-red-500/75 border-red-600/30",
          },
        ].map(({ label, classes }) => (
          <div key={label} className="flex items-center gap-1.5">
            <div className={`w-3 h-3 rounded-sm border ${classes}`} />
            <span className="text-xs text-theme-muted">{label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
